import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getOrderInspection, type OrderInspectionResponse } from '../api/endpoints.js';
import { useApiQuery } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  IdMono,
  Input,
  LoadingState,
  PageHead,
  StatusBadge,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';

export function OrderInspectPage(): JSX.Element {
  const { orderId: routeId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState(routeId ?? '');
  const orderId = routeId ?? '';
  const canInspect = useCan('order:inspect');

  const query = useApiQuery<OrderInspectionResponse>(
    (options) => getOrderInspection(options, orderId),
    [orderId],
  );

  function onSubmit(ev: FormEvent): void {
    ev.preventDefault();
    const trimmed = search.trim();
    if (!trimmed) return;
    navigate(`/orders/${encodeURIComponent(trimmed)}`);
  }

  return (
    <div>
      <PageHead
        crumb={
          orderId ? (
            <>
              <Link to="/orders" style={{ color: 'var(--text-tertiary)' }}>Siparişler</Link>
              <Icon.ChevronRight size={11} /> Detay
            </>
          ) : (
            <>Yönetim <Icon.ChevronRight size={11} /> Siparişler</>
          )
        }
        title={orderId ? 'Sipariş Detayı' : 'Siparişler'}
        subtitle="Sunucu projeksiyonundan gelen teslimat zaman çizelgesi. Provider iç yükü (raw payload) API tarafında filtrelenir — bu ekranda yalnızca operatör için güvenli alanlar gözükür."
      />

      <Card>
        <h2 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600 }}>Sipariş Ara</h2>
        <form onSubmit={onSubmit} aria-label="sipariş arama">
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <Input
                type="text"
                placeholder="sipariş id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="sipariş id"
                iconLeft={<Icon.Search size={14} />}
              />
            </div>
            <Button type="submit" variant="primary" icon={<Icon.ArrowUR size={14} />}>
              Aç
            </Button>
          </div>
        </form>
      </Card>

      {!orderId ? (
        <EmptyState
          label="Yukarıya bir sipariş id girin."
          hint="Doğru id formatı için müşteri detay sayfasındaki sipariş listesinden kopyalayabilirsiniz."
          icon={<Icon.Inbox size={22} />}
        />
      ) : null}
      {orderId && !canInspect ? <ErrorState reason="capability_missing:order:inspect" /> : null}
      {orderId && canInspect && query.kind === 'loading' ? <LoadingState /> : null}
      {orderId && canInspect && query.kind === 'error' ? (
        <ErrorState reason={query.reason} onRetry={query.reload} />
      ) : null}
      {orderId && canInspect && query.kind === 'ok' ? (
        <OrderDetail order={query.data.order} correlationId={query.data.correlationId} />
      ) : null}
    </div>
  );
}

function OrderDetail({
  order,
  correlationId,
}: {
  readonly order: OrderInspectionResponse['order'];
  readonly correlationId: string;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card flush>
        <CardHeader title="Sipariş" actions={<StatusBadge status={order.state} />} />
        <CardBody>
          <div className="dl-rows">
            <Row k="Id" v={<IdMono value={order.orderId} />} />
            <Row k="State" v={<StatusBadge status={order.state} />} />
            <Row k="SKU" v={<span className="t-mono">{order.sku ?? '—'}</span>} />
            <Row k="Provider" v={<span className="t-mono">{order.providerId ?? '—'}</span>} />
            <Row k="Actor" v={order.actorId ? <IdMono value={order.actorId} /> : '—'} />
            <Row k="Reservation" v={order.reservationId ? <IdMono value={order.reservationId} /> : '—'} />
            <Row k="Opened at" v={<span className="t-mono t-xs">{order.openedAt}</span>} />
            <Row k="Updated at" v={<span className="t-mono t-xs">{order.updatedAt}</span>} />
            <Row k="Closed at" v={<span className="t-mono t-xs">{order.closedAt ?? '—'}</span>} />
          </div>
          <div style={{ marginTop: 12 }}>
            <Banner tone="info">
              <span className="t-mono t-xs">correlation: {correlationId}</span>
            </Banner>
          </div>
        </CardBody>
      </Card>

      <Card flush>
        <CardHeader title="Outcome" />
        <CardBody>
          {order.outcome ? (
            <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(order.outcome, null, 2)}</pre>
          ) : (
            <EmptyState label="Henüz terminal outcome kaydı yok." />
          )}
        </CardBody>
      </Card>

      <Card flush>
        <CardHeader title="Teslimat Zaman Çizelgesi" />
        <CardBody tight style={{ padding: order.deliveryTimeline.length === 0 ? 20 : 0 }}>
          {order.deliveryTimeline.length === 0 ? (
            <EmptyState label="Teslimat olayı yok." />
          ) : (
            <Table dense>
              <THead>
                <TRow>
                  <TH>Zaman</TH>
                  <TH>Outcome</TH>
                  <TH>Provider</TH>
                  <TH>Konu / hata</TH>
                  <TH>From</TH>
                </TRow>
              </THead>
              <TBody>
                {order.deliveryTimeline.map((e, idx) => (
                  <TRow key={`${e.recordedAt}-${idx}`}>
                    <TD className="t-mono t-xs t-tertiary">{e.recordedAt}</TD>
                    <TD><OutcomeBadge kind={e.outcomeKind} /></TD>
                    <TD className="t-mono">{e.providerId}</TD>
                    <TD className="t-mono t-xs" style={{ wordBreak: 'break-all' }}>
                      {e.subject ?? e.failureReason ?? e.detail ?? '—'}
                    </TD>
                    <TD className="t-mono t-xs">{e.from ?? '—'}</TD>
                  </TRow>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Row({ k, v }: { readonly k: string; readonly v: React.ReactNode }): JSX.Element {
  return (
    <div className="dl-row">
      <div className="dl-key">{k}</div>
      <div className="dl-val">{v}</div>
    </div>
  );
}

function OutcomeBadge({ kind }: { readonly kind: string }): JSX.Element {
  switch (kind) {
    case 'delivered':     return <Badge tone="success" dot>{kind}</Badge>;
    case 'failed':        return <Badge tone="danger" dot>{kind}</Badge>;
    case 'pending_review':return <Badge tone="warn" dot>{kind}</Badge>;
    default:              return <Badge tone="neutral" dot>{kind}</Badge>;
  }
}
